/**
 * Camera-based barcode scanning using @zxing/browser.
 * Degrades gracefully when camera is unavailable or permission is denied.
 */
import { useState, useRef, useCallback, useEffect } from "react";

export function useBarcodeScan() {
  const [scanning, setScanning] = useState(false);
  const [cameraUnavailable, setCameraUnavailable] = useState(false);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);

  const stop = useCallback(() => {
    if (controlsRef.current) {
      try {
        controlsRef.current.stop();
      } catch {}
      controlsRef.current = null;
    }
    setScanning(false);
  }, []);

  const start = useCallback(async (videoEl, onResult) => {
    if (!videoEl) return;
    if (scanning) return;

    // Lazy-load @zxing/browser to keep the initial bundle light
    let BrowserMultiFormatReader;
    try {
      const mod = await import("@zxing/browser");
      BrowserMultiFormatReader = mod.BrowserMultiFormatReader;
    } catch {
      setCameraUnavailable(true);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraUnavailable(true);
      return;
    }

    try {
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;
      setScanning(true);

      const controls = await reader.decodeFromVideoDevice(
        undefined,
        videoEl,
        (result, _err, controls) => {
          if (result) {
            onResult(result.getText());
            controls.stop();
            setScanning(false);
          }
        }
      );
      controlsRef.current = controls;
    } catch {
      setCameraUnavailable(true);
      setScanning(false);
    }
  }, [scanning]);

  useEffect(() => () => stop(), [stop]);

  return { scanning, cameraUnavailable, start, stop };
}
